import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import PropTypes from 'prop-types';
import useLocalize from '../../utils/hooks/useLocalize';
import { formatFieldValue, getFieldLabel } from '../../utils/scan';
import './ScanCriteriaPopupModal.scss';

/**
 * ScanCriteriaPopupModal – displays the criteria of a scan in a read-only
 * modal popup.  Shown when the user clicks [詳細] on a scan card.
 * @param {Object}   props
 * @param {boolean}  props.open        – whether the modal is visible
 * @param {Function} props.onClose     – callback to close the modal
 * @param {Object}   props.scan        – scan object ({ id, name, fields, … })
 * @param {string}   [props.description] – optional description shown in sidebar
 * @param {Function} [props.onEdit]    – callback when edit button is clicked
 */
const ScanCriteriaPopupModal = ({
  open,
  onClose,
  onExited,
  scan,
  description,
  onEdit
}) => {
  const localize = useLocalize();

  /** Filter out fields that have no meaningful value to display. */
  const visibleFields = (scan?.fields || []).filter(
    (field) => formatFieldValue(field, localize) !== null
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited }}
      className="scan-criteria-popup-overlay"
      aria-labelledby="scan-criteria-popup-title"
    >
      <DialogContent>
        <div className="scan-criteria-popup">
          {/* ---- Two-column content ---- */}
          <div className="scan-criteria-popup-content">
            {/* Left sidebar */}
            <div className="scan-criteria-popup-sidebar">
              <div className="scan-criteria-popup-sidebar-heading">
                <h2
                  className="scan-criteria-popup-sidebar-title"
                  id="scan-criteria-popup-title"
                >
                  {localize('desktop.scans.scan.details')}
                </h2>
                {scan?.name && (
                  <p className="scan-criteria-popup-sidebar-scan-name">
                    {scan.name}
                  </p>
                )}
              </div>
              {description && (
                <p className="scan-criteria-popup-sidebar-description">
                  {description}
                </p>
              )}
            </div>

            {/* Right criteria area */}
            <div className="scan-criteria-popup-main">
              <button
                className="scan-criteria-popup-close-icon"
                onClick={onClose}
                aria-label={localize('button.label.close')}
              >
                <CloseIcon />
              </button>

              {visibleFields.length > 0 ? (
                <ul className="scan-criteria-popup-list">
                  {visibleFields.map((field) => (
                    <li key={field.id} className="scan-criteria-popup-item">
                      <span className="scan-criteria-popup-label">
                        {getFieldLabel(field.id, localize)}
                      </span>
                      <span className="scan-criteria-popup-separator">：</span>
                      <span className="scan-criteria-popup-value">
                        {formatFieldValue(field, localize)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="scan-criteria-popup-empty">
                  {localize('desktop.scans.criteria.popup.empty')}
                </div>
              )}
            </div>
          </div>

          {/* ---- Footer ---- */}
          <div className="scan-criteria-popup-footer">
            <button
              className="scan-criteria-popup-close-button"
              onClick={onClose}
            >
              {localize('button.label.close')}
            </button>
            <button
              className="scan-criteria-popup-edit-button"
              onClick={onEdit}
            >
              {localize('desktop.scans.criteria.popup.edit')}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

ScanCriteriaPopupModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onExited: PropTypes.func,
  scan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        type: PropTypes.string,
        values: PropTypes.array
      })
    )
  }),
  description: PropTypes.string,
  onEdit: PropTypes.func
};

export default ScanCriteriaPopupModal;
